// Skip logic for one contact on the Several or No Contacts story form (CP-2184), in the shape
// Stories::ExpandedSkipLogicRules builds: source_field_id is always the trigger question.
export interface SkipLogicField {
  id: number;
  priority: number;
}

export interface SkipLogicRule {
  source_field_id: number;
  destination_field_id: number;
  kind: 'hide_unless' | 'jump_to';
  sub_ratio_ids: number[];
}

export interface SkipLogicRules {
  fields: SkipLogicField[];
  rules: SkipLogicRule[];
}

// The answer choices (sub-ratio ids) the contact has chosen for a question.
export type AnswersFor = (fieldId: number) => number[];

function groupBy(rules: SkipLogicRule[], key: (rule: SkipLogicRule) => number) {
  const groups = new Map<number, SkipLogicRule[]>();
  for (const rule of rules) groups.set(key(rule), [...(groups.get(key(rule)) || []), rule]);
  return groups;
}

// The questions hidden for one contact. Questions are walked in survey order so that a question
// hidden for the contact no longer triggers anything with the answer it still holds.
// Like Single Contact mode, a Hide Unless rule that reveals a question wins over a Jump To past it.
export function hiddenFieldIds(
  fields: SkipLogicField[],
  rules: SkipLogicRule[],
  answersFor: AnswersFor,
): Set<number> {
  const priorities = new Map(fields.map((field) => [field.id, field.priority]));
  const hideUnlessByHidden = groupBy(
    rules.filter((rule) => rule.kind === 'hide_unless'),
    (rule) => rule.destination_field_id,
  );
  const jumpToByTrigger = groupBy(
    rules.filter((rule) => rule.kind === 'jump_to'),
    (rule) => rule.source_field_id,
  );
  const hidden = new Set<number>();
  const jumpedOver = new Set<number>();
  const isMet = (rule: SkipLogicRule) =>
    !hidden.has(rule.source_field_id) &&
    answersFor(rule.source_field_id).some((id) => rule.sub_ratio_ids.includes(id));

  for (const field of [...fields].sort((a, b) => a.priority - b.priority)) {
    const hideUnlessRules = hideUnlessByHidden.get(field.id);
    const revealed = hideUnlessRules?.some(isMet);

    if (hideUnlessRules ? !revealed : jumpedOver.has(field.id)) {
      hidden.add(field.id);
      continue;
    }

    for (const rule of (jumpToByTrigger.get(field.id) || []).filter(isMet)) {
      const destinationPriority = priorities.get(rule.destination_field_id) as number;
      for (const later of fields) {
        if (later.priority > field.priority && later.priority < destinationPriority) {
          jumpedOver.add(later.id);
        }
      }
    }
  }

  return hidden;
}
